import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Spinner,
  Button,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Tooltip,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Option,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MailRegular,
  ChatRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ClockRegular,
  ArrowSyncRegular,
  SendRegular,
  PersonRegular,
  InfoRegular,
  SignatureRegular,
  DocumentRegular,
  AlertRegular,
  ChevronDownRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import {
  getNotificationHistory,
  getNotificationTypes,
  sendManualNotification,
  resendNotification,
} from '../services/api';
import {
  Notification,
  NotificationTypeOption,
  NotificationType,
  NotificationRecipientType,
  NotificationStatus,
} from '../types';

interface NotificationHistoryPanelProps {
  taxReturnId: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  preparerName?: string;
  preparerEmail?: string;
  reviewerName?: string;
  reviewerEmail?: string;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px',
    color: tokens.colorNeutralForeground3,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  channelIcons: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  formField: {
    marginBottom: '16px',
  },
  fieldLabel: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: tokens.fontWeightSemibold,
  },
  recipientInfo: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

function getStatusColor(status?: NotificationStatus): 'success' | 'danger' | 'warning' | 'subtle' {
  switch (status) {
    case 'sent':
    case 'delivered':
      return 'success';
    case 'failed':
      return 'danger';
    case 'pending':
      return 'warning';
    default:
      return 'subtle';
  }
}

function getNotificationTypeIcon(type: NotificationType) {
  switch (type) {
    case 'signature_request_sent':
    case 'signature_request_resent':
    case 'signature_completed':
    case 'signature_declined':
    case 'signature_expired':
    case 'signature_voided':
      return <SignatureRegular style={{ fontSize: '14px', color: '#8764b8' }} />;
    case 'documents_pending':
    case 'documents_received':
    case 'document_reminder':
    case 'document_checklist':
    case 'completed_return_copy':
      return <DocumentRegular style={{ fontSize: '14px', color: '#0078d4' }} />;
    case 'efile_accepted':
    case 'review_approved':
      return <CheckmarkCircleRegular style={{ fontSize: '14px', color: '#107c10' }} />;
    case 'efile_rejected':
    case 'changes_needed':
      return <AlertRegular style={{ fontSize: '14px', color: '#d13438' }} />;
    default:
      return <MailRegular style={{ fontSize: '14px', color: 'var(--tl-text-secondary)' }} />;
  }
}

export default function NotificationHistoryPanel({
  taxReturnId,
  clientName,
  clientEmail,
  clientPhone,
  preparerName,
  preparerEmail,
  reviewerName,
  reviewerEmail,
}: NotificationHistoryPanelProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const notificationTypeLabels: Record<NotificationType, string> = useMemo(() => ({
    documents_pending: t('notificationHistoryPanel.notificationTypeLabels.documentsPending'),
    documents_received: t('notificationHistoryPanel.notificationTypeLabels.documentsReceived'),
    ready_for_review: t('notificationHistoryPanel.notificationTypeLabels.readyForReview'),
    changes_needed: t('notificationHistoryPanel.notificationTypeLabels.changesNeeded'),
    review_approved: t('notificationHistoryPanel.notificationTypeLabels.reviewApproved'),
    ready_for_signature: t('notificationHistoryPanel.notificationTypeLabels.readyForSignature'),
    efile_accepted: t('notificationHistoryPanel.notificationTypeLabels.efileAccepted'),
    efile_rejected: t('notificationHistoryPanel.notificationTypeLabels.efileRejected'),
    return_results_summary: t('notificationHistoryPanel.notificationTypeLabels.returnResultsSummary'),
    signature_request_sent: t('notificationHistoryPanel.notificationTypeLabels.signatureRequestSent'),
    signature_request_resent: t('notificationHistoryPanel.notificationTypeLabels.signatureRequestResent'),
    signature_completed: t('notificationHistoryPanel.notificationTypeLabels.signatureCompleted'),
    signature_declined: t('notificationHistoryPanel.notificationTypeLabels.signatureDeclined'),
    signature_expired: t('notificationHistoryPanel.notificationTypeLabels.signatureExpired'),
    signature_voided: t('notificationHistoryPanel.notificationTypeLabels.signatureVoided'),
    document_reminder: t('notificationHistoryPanel.notificationTypeLabels.documentReminder'),
    document_checklist: t('notificationHistoryPanel.notificationTypeLabels.documentChecklist'),
    completed_return_copy: t('notificationHistoryPanel.notificationTypeLabels.completedReturnCopy'),
    general_message: t('notificationHistoryPanel.notificationTypeLabels.generalMessage'),
    review_assigned: t('notificationHistoryPanel.notificationTypeLabels.reviewAssigned'),
    status_update: t('notificationHistoryPanel.notificationTypeLabels.statusUpdate'),
    appointment_reminder: t('notificationHistoryPanel.notificationTypeLabels.appointmentReminder'),
    custom: t('notificationHistoryPanel.notificationTypeLabels.custom'),
  }), [t]);

  const recipientTypeLabels: Record<NotificationRecipientType, string> = useMemo(() => ({
    client: t('notificationHistoryPanel.recipientTypeLabels.client'),
    preparer: t('notificationHistoryPanel.recipientTypeLabels.preparer'),
    reviewer: t('notificationHistoryPanel.recipientTypeLabels.reviewer'),
  }), [t]);

  // Channel words ("Email"/"SMS") reused across the dialog, the badges and the expanded row --
  // one translation each, not a copy per usage site.
  const channelLabels: Record<'email' | 'sms', string> = useMemo(() => ({
    email: t('notificationHistoryPanel.channelLabels.email'),
    sms: t('notificationHistoryPanel.channelLabels.sms'),
  }), [t]);

  // Delivery-status words shown in the per-channel tooltips (raw NotificationStatus values) and
  // reused for the overall Sent/Failed/Pending badge text.
  const notificationStatusLabels: Record<NotificationStatus, string> = useMemo(() => ({
    pending: t('notificationHistoryPanel.statusLabels.pending'),
    sent: t('notificationHistoryPanel.statusLabels.sent'),
    failed: t('notificationHistoryPanel.statusLabels.failed'),
    delivered: t('notificationHistoryPanel.statusLabels.delivered'),
  }), [t]);
  const notAvailableLabel = t('notificationHistoryPanel.statusLabels.notAvailable');
  const getChannelStatusLabel = (status?: NotificationStatus) =>
    status ? notificationStatusLabels[status] : notAvailableLabel;

  // State for expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // State for manual send dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<NotificationType | ''>('');
  const [selectedRecipient, setSelectedRecipient] = useState<NotificationRecipientType | ''>('');
  const [customMessage, setCustomMessage] = useState('');
  const [customEmailSubject, setCustomEmailSubject] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Get contact info for selected recipient
  const getRecipientContact = () => {
    switch (selectedRecipient) {
      case 'client':
        return { email: clientEmail, phone: clientPhone, name: clientName };
      case 'preparer':
        return { email: preparerEmail, phone: undefined, name: preparerName };
      case 'reviewer':
        return { email: reviewerEmail, phone: undefined, name: reviewerName };
      default:
        return { email: undefined, phone: undefined, name: undefined };
    }
  };

  // Fetch notification history
  const { data: notifications, isLoading, refetch } = useQuery<Notification[]>({
    queryKey: ['notificationHistory', taxReturnId],
    queryFn: () => getNotificationHistory(taxReturnId),
    enabled: !!taxReturnId,
  });

  // Fetch notification types
  const { data: notificationTypes } = useQuery<NotificationTypeOption[]>({
    queryKey: ['notificationTypes'],
    queryFn: getNotificationTypes,
  });

  // Send manual notification mutation
  const sendMutation = useMutation({
    mutationFn: sendManualNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationHistory', taxReturnId] });
      setSendDialogOpen(false);
      setSelectedType('');
      setSelectedRecipient('');
      setCustomMessage('');
      setCustomEmailSubject('');
      setAdditionalNotes('');
    },
  });

  // Resend notification mutation
  const resendMutation = useMutation({
    mutationFn: ({ notificationId }: { notificationId: string }) =>
      resendNotification(notificationId, { taxReturnId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationHistory', taxReturnId] });
    },
  });

  const handleSendNotification = () => {
    if (!selectedType || !selectedRecipient) return;

    sendMutation.mutate({
      taxReturnId,
      notificationType: selectedType,
      recipientType: selectedRecipient,
      customMessage: customMessage || undefined,
      customEmailSubject: customEmailSubject || undefined,
      additionalNotes: additionalNotes || undefined,
    });
  };

  const handleResend = (notificationId: string) => {
    resendMutation.mutate({ notificationId });
  };

  // Check if a notification can be resent (only failed ones)
  const canResend = (notification: Notification) => {
    return notification.emailStatus === 'failed' || notification.smsStatus === 'failed';
  };

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spinner size="small" label={t('notificationHistoryPanel.loading')} />
      </div>
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.title}>
          <MailRegular />
          {t('notificationHistoryPanel.title')}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            onClick={() => refetch()}
            size="small"
          >
            {t('notificationHistoryPanel.refreshButton')}
          </Button>
          <Dialog open={sendDialogOpen} onOpenChange={(_, data) => setSendDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button
                appearance="primary"
                icon={<SendRegular />}
                size="small"
              >
                {t('notificationHistoryPanel.sendButton')}
              </Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{t('notificationHistoryPanel.sendDialog.title')}</DialogTitle>
                <DialogContent>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('notificationHistoryPanel.sendDialog.typeLabel')}</label>
                    <Dropdown
                      placeholder={t('notificationHistoryPanel.sendDialog.typePlaceholder')}
                      value={selectedType}
                      onOptionSelect={(_, data) => {
                        const type = data.optionValue as NotificationType;
                        setSelectedType(type);
                        // Auto-select default recipient if available
                        const typeConfig = notificationTypes?.find(nt => nt.id === type);
                        if (typeConfig?.defaultRecipient) {
                          setSelectedRecipient(typeConfig.defaultRecipient);
                        }
                      }}
                      style={{ width: '100%' }}
                    >
                      {(notificationTypes || []).map(type => (
                        <Option key={type.id} value={type.id} text={type.name}>
                          <div>
                            <div>{type.name}</div>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                              {type.description}
                            </div>
                          </div>
                        </Option>
                      ))}
                    </Dropdown>
                  </div>

                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('notificationHistoryPanel.sendDialog.recipientLabel')}</label>
                    <Dropdown
                      placeholder={t('notificationHistoryPanel.sendDialog.recipientPlaceholder')}
                      value={selectedRecipient}
                      onOptionSelect={(_, data) => setSelectedRecipient(data.optionValue as NotificationRecipientType)}
                      style={{ width: '100%' }}
                    >
                      <Option value="client" text={recipientTypeLabels.client}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <PersonRegular />
                          <div>
                            <div>{recipientTypeLabels.client}</div>
                            {clientName && (
                              <div className={styles.recipientInfo}>{clientName}</div>
                            )}
                            <div className={styles.recipientInfo}>
                              {clientEmail ? <><MailRegular style={{ fontSize: '10px' }} /> {channelLabels.email}</> : null}
                              {clientEmail && clientPhone ? ' • ' : null}
                              {clientPhone ? <><ChatRegular style={{ fontSize: '10px' }} /> {channelLabels.sms}</> : null}
                              {!clientEmail && !clientPhone ? t('notificationHistoryPanel.sendDialog.contactNoInfo') : null}
                            </div>
                          </div>
                        </div>
                      </Option>
                      <Option value="preparer" text={recipientTypeLabels.preparer}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <PersonRegular />
                          <div>
                            <div>{recipientTypeLabels.preparer}</div>
                            {preparerName && (
                              <div className={styles.recipientInfo}>{preparerName}</div>
                            )}
                            <div className={styles.recipientInfo}>
                              {preparerEmail ? <><MailRegular style={{ fontSize: '10px' }} /> {t('notificationHistoryPanel.sendDialog.contactEmailOnly')}</> : t('notificationHistoryPanel.sendDialog.contactNoEmail')}
                            </div>
                          </div>
                        </div>
                      </Option>
                      <Option value="reviewer" text={recipientTypeLabels.reviewer}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <PersonRegular />
                          <div>
                            <div>{recipientTypeLabels.reviewer}</div>
                            {reviewerName && (
                              <div className={styles.recipientInfo}>{reviewerName}</div>
                            )}
                            <div className={styles.recipientInfo}>
                              {reviewerEmail ? <><MailRegular style={{ fontSize: '10px' }} /> {t('notificationHistoryPanel.sendDialog.contactEmailOnly')}</> : t('notificationHistoryPanel.sendDialog.contactNoEmail')}
                            </div>
                          </div>
                        </div>
                      </Option>
                    </Dropdown>
                  </div>

                  {/* Contact Availability Alert */}
                  {selectedRecipient && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: !getRecipientContact().email && !getRecipientContact().phone
                        ? '#fde7e9'
                        : !getRecipientContact().phone && selectedRecipient === 'client'
                        ? '#fff4ce'
                        : tokens.colorNeutralBackground2,
                      borderRadius: '4px',
                      marginBottom: '16px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <InfoRegular />
                        <div style={{ fontSize: '13px' }}>
                          {!getRecipientContact().email && !getRecipientContact().phone ? (
                            <span style={{ color: '#a4262c' }}>{t('notificationHistoryPanel.sendDialog.noContactInfo')}</span>
                          ) : (
                            <>
                              <strong>{t('notificationHistoryPanel.sendDialog.willSendVia')}</strong>{' '}
                              {getRecipientContact().email && channelLabels.email}
                              {getRecipientContact().email && getRecipientContact().phone && ' + '}
                              {getRecipientContact().phone && channelLabels.sms}
                              {!getRecipientContact().phone && selectedRecipient === 'client' && (
                                <span style={{ color: '#8a6914' }}> {t('notificationHistoryPanel.sendDialog.smsWillBeSkipped')}</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Custom/Edit SMS Message */}
                  {selectedRecipient === 'client' && getRecipientContact().phone && (
                    <div className={styles.formField}>
                      <label className={styles.fieldLabel}>{t('notificationHistoryPanel.sendDialog.smsMessageLabel')}</label>
                      <Textarea
                        value={customMessage}
                        onChange={(_, data) => setCustomMessage(data.value)}
                        placeholder={t('notificationHistoryPanel.sendDialog.smsMessagePlaceholder')}
                        rows={3}
                        style={{ width: '100%' }}
                      />
                      <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                        {t('notificationHistoryPanel.sendDialog.smsCharCount', { count: customMessage.length })} {customMessage.length > 160 && t('notificationHistoryPanel.sendDialog.smsCharSplitWarning')}
                      </div>
                    </div>
                  )}

                  {/* Custom Email Subject */}
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('notificationHistoryPanel.sendDialog.emailSubjectLabel')}</label>
                    <Textarea
                      value={customEmailSubject}
                      onChange={(_, data) => setCustomEmailSubject(data.value)}
                      placeholder={t('notificationHistoryPanel.sendDialog.emailSubjectPlaceholder')}
                      rows={1}
                      style={{ width: '100%' }}
                    />
                  </div>

                  {/* Additional Notes - shown for ALL notification types */}
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('notificationHistoryPanel.sendDialog.notesLabel')}</label>
                    <Textarea
                      value={additionalNotes}
                      onChange={(_, data) => setAdditionalNotes(data.value)}
                      placeholder={t('notificationHistoryPanel.sendDialog.notesPlaceholder')}
                      rows={3}
                      style={{ width: '100%' }}
                    />
                    <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                      {t('notificationHistoryPanel.sendDialog.notesHint')}
                    </div>
                  </div>

                  <div style={{
                    padding: '12px',
                    backgroundColor: tokens.colorNeutralBackground2,
                    borderRadius: '4px',
                    marginTop: '16px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <InfoRegular />
                      <strong>{t('notificationHistoryPanel.sendDialog.noteHeading')}</strong>
                    </div>
                    <div style={{ fontSize: '13px', color: tokens.colorNeutralForeground3 }}>
                      {t('notificationHistoryPanel.sendDialog.noteBodyBase')}{' '}
                      {selectedRecipient === 'client' && getRecipientContact().phone
                        ? t('notificationHistoryPanel.sendDialog.noteBodySmsCustomizable')
                        : selectedRecipient === 'client' && !getRecipientContact().phone
                        ? t('notificationHistoryPanel.sendDialog.noteBodyAddPhone')
                        : t('notificationHistoryPanel.sendDialog.noteBodyStaffEmailOnly')}
                    </div>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button
                    appearance="secondary"
                    onClick={() => setSendDialogOpen(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    appearance="primary"
                    icon={<SendRegular />}
                    onClick={handleSendNotification}
                    disabled={!selectedType || !selectedRecipient || sendMutation.isPending}
                  >
                    {sendMutation.isPending ? t('notificationHistoryPanel.sendDialog.sendingButton') : t('notificationHistoryPanel.sendDialog.sendSubmitButton')}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      </div>

      {notifications && notifications.length > 0 ? (
        <Table size="small" style={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t('notificationHistoryPanel.table.columns.type')}</TableHeaderCell>
              <TableHeaderCell>{t('notificationHistoryPanel.table.columns.recipient')}</TableHeaderCell>
              <TableHeaderCell>{t('notificationHistoryPanel.table.columns.channel')}</TableHeaderCell>
              <TableHeaderCell>{t('notificationHistoryPanel.table.columns.status')}</TableHeaderCell>
              <TableHeaderCell>{t('notificationHistoryPanel.table.columns.sent')}</TableHeaderCell>
              <TableHeaderCell>{t('notificationHistoryPanel.table.columns.actions')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notifications.map((notification) => {
              const hasContent = !!(notification.emailSubject || notification.emailBody || notification.smsBody || notification.emailError || notification.smsError);
              const isExpanded = expandedRows.has(notification.id);

              return (
                <React.Fragment key={notification.id}>
                  <TableRow
                    style={hasContent ? { cursor: 'pointer' } : undefined}
                    onClick={hasContent ? () => toggleRow(notification.id) : undefined}
                  >
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}>
                        {hasContent ? (
                          isExpanded ? <ChevronDownRegular style={{ fontSize: '12px' }} /> : <ChevronRightRegular style={{ fontSize: '12px' }} />
                        ) : (
                          <span style={{ width: '12px', display: 'inline-block' }} />
                        )}
                        {getNotificationTypeIcon(notification.notificationType)}
                        {notificationTypeLabels[notification.notificationType] || notification.notificationType}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {recipientTypeLabels[notification.recipientType]}
                        </div>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                          {notification.recipientName}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.channelIcons}>
                        {(notification.channel === 'email' || notification.channel === 'both') && (
                          <Tooltip content={t('notificationHistoryPanel.table.emailStatusTooltip', { status: getChannelStatusLabel(notification.emailStatus) })} relationship="label">
                            <Badge
                              appearance="outline"
                              color={getStatusColor(notification.emailStatus)}
                              icon={<MailRegular />}
                              size="small"
                            />
                          </Tooltip>
                        )}
                        {(notification.channel === 'sms' || notification.channel === 'both') && (
                          <Tooltip content={t('notificationHistoryPanel.table.smsStatusTooltip', { status: getChannelStatusLabel(notification.smsStatus) })} relationship="label">
                            <Badge
                              appearance="outline"
                              color={getStatusColor(notification.smsStatus)}
                              icon={<ChatRegular />}
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.statusBadge}>
                        {notification.emailStatus === 'sent' || notification.smsStatus === 'sent' ? (
                          <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
                            {notificationStatusLabels.sent}
                          </Badge>
                        ) : notification.emailStatus === 'failed' || notification.smsStatus === 'failed' ? (
                          <Tooltip
                            content={notification.emailError || notification.smsError || t('notificationHistoryPanel.table.deliveryFailedTooltip')}
                            relationship="label"
                          >
                            <Badge appearance="filled" color="danger" icon={<DismissCircleRegular />}>
                              {notificationStatusLabels.failed}
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Badge appearance="outline" color="warning" icon={<ClockRegular />}>
                            {notificationStatusLabels.pending}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {notification.sentAt
                        ? format(new Date(notification.sentAt), 'MMM d, yyyy h:mm a')
                        : format(new Date(notification.createdAt), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>
                      {canResend(notification) && (
                        <Button
                          appearance="subtle"
                          icon={<ArrowSyncRegular />}
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleResend(notification.id); }}
                          disabled={resendMutation.isPending}
                        >
                          {t('notificationHistoryPanel.table.resendButton')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={6} style={{ overflow: 'hidden' }}>
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: tokens.colorNeutralBackground2,
                          borderRadius: '4px',
                          margin: '4px 0',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          overflow: 'hidden',
                        }}>
                          {(notification.emailSubject || notification.emailBody) && (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <MailRegular style={{ fontSize: '14px' }} />
                                <strong style={{ fontSize: '13px' }}>{channelLabels.email}</strong>
                              </div>
                              {notification.emailSubject && (
                                <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                                  {/* notification.emailSubject is the actual sent email's subject -- data, not chrome */}
                                  <strong>{t('notificationHistoryPanel.table.expandedSubjectLabel')}</strong> {notification.emailSubject}
                                </div>
                              )}
                              {notification.emailBody && (
                                <div
                                  style={{
                                    fontSize: '13px',
                                    padding: '8px',
                                    backgroundColor: tokens.colorNeutralBackground1,
                                    borderRadius: '4px',
                                    border: `1px solid ${tokens.colorNeutralStroke2}`,
                                    maxHeight: '200px',
                                    overflow: 'auto',
                                    wordBreak: 'break-word',
                                  }}
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notification.emailBody, {
                                    ALLOWED_TAGS: ['div', 'span', 'p', 'h1', 'h2', 'h3', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'a', 'table', 'tr', 'td', 'th', 'tbody', 'thead'],
                                    ALLOWED_ATTR: ['style', 'href', 'class']
                                  }) }}
                                />
                              )}
                              {/* notification.emailError is the sent-attempt's own error message (from Graph) -- data, not chrome */}
                              {notification.emailError && (
                                <div style={{ fontSize: '12px', color: '#d13438', marginTop: '4px' }}>
                                  <DismissCircleRegular style={{ fontSize: '12px' }} /> {notification.emailError}
                                </div>
                              )}
                            </div>
                          )}
                          {notification.smsBody && (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <ChatRegular style={{ fontSize: '14px' }} />
                                <strong style={{ fontSize: '13px' }}>{channelLabels.sms}</strong>
                              </div>
                              <div style={{
                                fontSize: '13px',
                                padding: '8px',
                                backgroundColor: tokens.colorNeutralBackground1,
                                borderRadius: '4px',
                                border: `1px solid ${tokens.colorNeutralStroke2}`,
                                whiteSpace: 'pre-wrap',
                              }}>
                                {notification.smsBody}
                              </div>
                              {notification.smsError && (
                                <div style={{ fontSize: '12px', color: '#d13438', marginTop: '4px' }}>
                                  <DismissCircleRegular style={{ fontSize: '12px' }} /> {notification.smsError}
                                </div>
                              )}
                            </div>
                          )}
                          {!notification.emailSubject && !notification.emailBody && !notification.smsBody && (notification.emailError || notification.smsError) && (
                            <div>
                              {/* Error prefixes are chrome; notification.emailError/smsError are the sent attempt's own error text -- data */}
                              {notification.emailError && (
                                <div style={{ fontSize: '12px', color: '#d13438' }}>
                                  <MailRegular style={{ fontSize: '12px' }} /> {t('notificationHistoryPanel.table.expandedEmailErrorPrefix')} {notification.emailError}
                                </div>
                              )}
                              {notification.smsError && (
                                <div style={{ fontSize: '12px', color: '#d13438' }}>
                                  <ChatRegular style={{ fontSize: '12px' }} /> {t('notificationHistoryPanel.table.expandedSmsErrorPrefix')} {notification.smsError}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className={styles.emptyState}>
          <MailRegular style={{ fontSize: '32px', marginBottom: '8px' }} />
          <div>{t('notificationHistoryPanel.emptyState.title')}</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            {t('notificationHistoryPanel.emptyState.hint')}
          </div>
        </div>
      )}
    </div>
  );
}
